import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, Trash2, Palette, Download, Settings2 } from 'lucide-react'

const ConfigPanel = ({ items, onChange }) => {
  // 本地状态管理，避免直接修改props
  const [localItems, setLocalItems] = useState([...items])

  // 示例转盘配置
  const exampleWheels = [
    {
      name: '今天吃什么',
      items: [
        { id: 1, text: '中餐', color: '#ef4444', weight: 1 },
        { id: 2, text: '西餐', color: '#3b82f6', weight: 1 },
        { id: 3, text: '日料', color: '#22c55e', weight: 1 },
        { id: 4, text: '韩料', color: '#f97316', weight: 1 },
        { id: 5, text: '泰餐', color: '#8b5cf6', weight: 1 },
        { id: 6, text: '火锅', color: '#ec4899', weight: 1 }
      ]
    },
    {
      name: '周末活动',
      items: [
        { id: 1, text: '看电影', color: '#8b5cf6', weight: 1 },
        { id: 2, text: '去公园', color: '#22c55e', weight: 1 },
        { id: 3, text: '宅在家', color: '#f97316', weight: 1 },
        { id: 4, text: '逛街', color: '#ec4899', weight: 1 },
        { id: 5, text: '运动', color: '#06b6d4', weight: 1 },
        { id: 6, text: '聚餐', color: '#eab308', weight: 1 }
      ]
    },
    {
      name: '学习计划',
      items: [
        { id: 1, text: '编程', color: '#6366f1', weight: 1 },
        { id: 2, text: '阅读', color: '#ec4899', weight: 1 },
        { id: 3, text: '运动', color: '#06b6d4', weight: 1 },
        { id: 4, text: '绘画', color: '#84cc16', weight: 1 },
        { id: 5, text: '音乐', color: '#f59e0b', weight: 1 },
        { id: 6, text: '写作', color: '#8b5cf6', weight: 1 }
      ]
    },
    {
      name: '游戏选择',
      items: [
        { id: 1, text: '王者荣耀', color: '#eab308', weight: 1 },
        { id: 2, text: '原神', color: '#84cc16', weight: 1 },
        { id: 3, text: 'LOL', color: '#f59e0b', weight: 1 },
        { id: 4, text: '和平精英', color: '#ef4444', weight: 1 },
        { id: 5, text: '崩坏3', color: '#8b5cf6', weight: 1 },
        { id: 6, text: 'CSGO', color: '#6366f1', weight: 1 }
      ]
    }
  ]

  // 预定义的颜色调色板，供用户选择
  const colors = [
    '#ef4444', '#f97316', '#eab308', '#22c55e', 
    '#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4',
    '#84cc16', '#f59e0b', '#6366f1'
  ]

  // 计算总权重
  const totalWeight = localItems.reduce((sum, item) => sum + (item.weight || 1), 0)

  // 处理选项修改（文字、颜色或权重）
  const handleItemChange = (index, field, value) => {
    const newItems = [...localItems] // 创建新数组避免直接修改
    newItems[index] = { ...newItems[index], [field]: value } // 使用展开运算符更新特定字段
    setLocalItems(newItems)
    onChange(newItems) // 通知父组件更新
  }

  // 添加新选项
  const addItem = () => {
    const newItem = {
      id: Date.now(), // 使用时间戳作为唯一ID
      text: `选项${localItems.length + 1}`, // 默认文字
      color: colors[localItems.length % colors.length], // 循环使用颜色
      weight: 1 // 默认权重为1
    }
    const newItems = [...localItems, newItem]
    setLocalItems(newItems)
    onChange(newItems)
  }

  // 删除选项（至少保留2个）
  const removeItem = (index) => {
    if (localItems.length <= 2) return // 最少保留2个选项
    const newItems = localItems.filter((_, i) => i !== index) // 过滤掉指定索引的项
    setLocalItems(newItems)
    onChange(newItems)
  }

  // 加载示例转盘
  const loadExampleWheel = (example) => {
    const newItems = example.items.map((item, index) => ({
      ...item,
      id: Date.now() + index // 生成新的ID避免冲突
    }))
    setLocalItems(newItems)
    onChange(newItems)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl p-4 sm:p-6 border border-slate-200 max-w-4xl mx-auto overflow-hidden shadow-sm"
    >
      <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 sm:mb-6 text-center flex items-center justify-center gap-2">
        <Settings2 className="w-6 h-6 text-theme-500" />
        转盘设置
      </h3>
      
      
      <div className="space-y-3 sm:space-y-4 max-h-[60vh] overflow-y-auto overflow-x-hidden pr-2">
        <AnimatePresence>
          {localItems.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-slate-50 rounded-xl p-3 sm:p-4 overflow-hidden border border-slate-200"
            >
              {/* 移动端布局 */}
              <div className="block sm:hidden space-y-3 w-full overflow-hidden">
                {/* 第一行：颜色选择器 */}
                <div className="flex items-center gap-2">
                  <Palette className="w-4 h-4 text-slate-600 flex-shrink-0" />
                  <div className="flex gap-1 flex-wrap flex-1">
                    {colors.map((color) => (
                      <button
                        key={color}
                        onClick={() => handleItemChange(index, 'color', color)}
                        className={`w-5 h-5 rounded-full border-2 ${
                          item.color === color ? 'border-theme-400' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>

                {/* 第二行：文字输入 */}
                <input
                  type="text"
                  value={item.text}
                  onChange={(e) => handleItemChange(index, 'text', e.target.value)}
                  className="w-full px-3 py-2 bg-white text-slate-800 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-theme-400 text-sm"
                  placeholder="输入选项文字"
                />

                {/* 第三行：权重设置和删除按钮 */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <label className="text-slate-600 text-xs">权重:</label>
                    <input
                      type="number"
                      min="0.1"
                      max="10"
                      step="0.1"
                      value={item.weight || 1}
                      onChange={(e) => handleItemChange(index, 'weight', parseFloat(e.target.value) || 1)}
                      className="w-12 px-1 py-1 bg-white text-slate-800 rounded border border-slate-300 focus:outline-none focus:ring-2 focus:ring-theme-400 text-center text-xs"
                    />
                    <div className="text-xs text-slate-500 min-w-[35px]">
                      {((item.weight || 1) / totalWeight * 100).toFixed(1)}%
                    </div>
                  </div>
                  
                  {localItems.length > 2 && (
                    <button
                      onClick={() => removeItem(index)}
                      className="p-1.5 text-red-500 hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* 桌面端布局 */}
              <div className="hidden sm:block w-full overflow-hidden space-y-3">
                {/* 第一行：颜色选择器 */}
                <div className="flex items-center gap-2">
                  <Palette className="w-5 h-5 text-slate-600 flex-shrink-0" />
                  <div className="flex gap-1 flex-wrap">
                    {colors.map((color) => (
                      <button
                        key={color}
                        onClick={() => handleItemChange(index, 'color', color)}
                        className={`w-6 h-6 rounded-full border-2 ${
                          item.color === color ? 'border-theme-400' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>

                {/* 第二行：文字输入、权重设置和删除按钮 */}
                <div className="flex items-center gap-3 sm:gap-4">
                  {/* 文字输入 */}
                  <input
                    type="text"
                    value={item.text}
                    onChange={(e) => handleItemChange(index, 'text', e.target.value)}
                    className="flex-1 min-w-0 px-4 py-2 bg-white text-slate-800 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-theme-400 overflow-hidden"
                    placeholder="输入选项文字"
                  />

                  {/* 权重设置 */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <label className="text-slate-600 text-sm">权重:</label>
                    <input
                      type="number"
                      min="0.1"
                      max="10"
                      step="0.1"
                      value={item.weight || 1}
                      onChange={(e) => handleItemChange(index, 'weight', parseFloat(e.target.value) || 1)}
                      className="w-16 px-2 py-1 bg-white text-slate-800 rounded border border-slate-300 focus:outline-none focus:ring-2 focus:ring-theme-400 text-center"
                    />
                    <div className="text-xs text-slate-500 min-w-[40px]">
                      {((item.weight || 1) / totalWeight * 100).toFixed(1)}%
                    </div>
                  </div>

                  {/* 删除按钮 */}
                  {localItems.length > 2 && (
                    <button
                      onClick={() => removeItem(index)}
                      className="p-2 text-red-500 hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors flex-shrink-0"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* 添加按钮 */}
        {localItems.length < 30 && (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={addItem}
            className="w-full py-3 bg-theme-500 text-white font-bold rounded-xl hover:bg-theme-600 transition-all duration-300 flex items-center justify-center gap-2"
          >
            <Plus className="w-5 h-5" />
            添加选项
          </motion.button>
        )}
      </div>

      {/* 提示信息 */}
      <div className="mt-4 text-center text-slate-500 text-xs sm:text-sm">
        最多支持30个选项，最少需要2个选项
      </div>
    </motion.div>
  )
}

export default ConfigPanel
